import { useAuth } from './lib/auth';
import { PortalLayout } from './components/PortalLayout';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { DashboardPage } from './pages/DashboardPage';
import { ProfilePage } from './pages/ProfilePage';
import { DirectoryPage } from './pages/DirectoryPage';
import { EventsPage } from './pages/EventsPage';
import { DonationsPage } from './pages/DonationsPage';
import { YahrzeitsPage } from './pages/YahrzeitsPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { BillingPage } from './pages/BillingPage';
import { HonorsPage } from './pages/HonorsPage';
import { MySeatsPage } from './pages/MySeatsPage';
import { SchoolPage } from './pages/SchoolPage';
import { CemeteryPage } from './pages/CemeteryPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { useState, useEffect } from 'react';

type Page = 'login' | 'register' | 'forgot-password' | 'reset-password' | 'dashboard' | 'profile' | 'directory' | 'events' | 'donate' | 'yahrzeits' | 'notifications' | 'billing' | 'honors' | 'my-seats' | 'school' | 'cemetery';

function App() {
  const { user, isLoading, isAuthenticated } = useAuth();
  const [currentPage, setCurrentPage] = useState<Page>('dashboard');

  // Simple hash-based routing
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1) || 'dashboard';
      const page = hash.split('?')[0] ?? hash;
      if (['login', 'register', 'forgot-password', 'reset-password', 'dashboard', 'profile', 'directory', 'events', 'donate', 'yahrzeits', 'notifications', 'billing', 'honors', 'my-seats', 'school', 'cemetery'].includes(page)) {
        setCurrentPage(page as Page);
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const navigate = (page: Page) => {
    window.location.hash = page;
    setCurrentPage(page);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  // Unauthenticated routes
  if (!isAuthenticated) {
    if (currentPage === 'register') {
      return <RegisterPage onNavigate={navigate} />;
    }
    if (currentPage === 'forgot-password') {
      return <ForgotPasswordPage onNavigate={navigate} />;
    }
    if (currentPage === 'reset-password') {
      return <ResetPasswordPage onNavigate={navigate} />;
    }
    return <LoginPage onNavigate={navigate} />;
  }

  // Authenticated routes
  const renderPage = () => {
    switch (currentPage) {
      case 'profile':
        return <ProfilePage />;
      case 'directory':
        return <DirectoryPage />;
      case 'events':
        return <EventsPage />;
      case 'donate':
        return <DonationsPage />;
      case 'yahrzeits':
        return <YahrzeitsPage />;
      case 'notifications':
        return <NotificationsPage />;
      case 'billing':
        return <BillingPage />;
      case 'honors':
        return <HonorsPage />;
      case 'my-seats':
        return <MySeatsPage />;
      case 'school':
        return <SchoolPage />;
      case 'cemetery':
        return <CemeteryPage />;
      default:
        return <DashboardPage />;
    }
  };

  return (
    <PortalLayout currentPage={currentPage} onNavigate={navigate} user={user!}>
      {renderPage()}
    </PortalLayout>
  );
}

export default App;
